import { Former } from './Former'
import { str } from '@/js/formers/op-build-helpers'
import _isEmpty from 'lodash/isEmpty'

/**
 * @typedef VerificationCDDFormerAttrs
 * @type {object}
 */

/**
 * Collects the attributes for sticker-related operations
 * @class
 * @implements {Former}
 */
export class VerificationCDDFormer extends Former {
  /** @type {VerificationCDDFormerAttrs} */
  attrs = _isEmpty(this.attrs) ? this._defaultAttrs : this.attrs

  _opBuilder = this._opBuilder || this._buildOpCreate

  get _defaultAttrs () {
    return {
      newPlaceOfBirth: '',
      imagesData: {
        documentImages: [],
        selfie: '',
        documentDetails: {},
      },
      documents: {
        name: false,
        placeOfBirth: false,
        birthDate: false,
        nationalities: false,
        address: false,
        email: false,
      },
      commonRisk: {
        qus01: '',
        qus02: '',
        qus03: '',
      },
      geoRisk: {
        qus01: '',
      },
      productRisk: {
        qus01: '',
        qus02: '',
        qus03: '',
        qus04: '',
        qus05: '',
        qus06: '',
      },
      pep: {
        qus01: '',
        qus02: '',
        pepInfo: '',
        countryRisk: '',
      },
      checkList: {
        qus03: '',
        qus04: '',
        description: '',
        riskFactors: '',
      },
      riskAssmnt: {
        comments: '',
        subscriber: '',
        signatureDate: '',
        lowRisk: false,
        generalRisk: false,
        highRisk: false,
      },
      rejectReason: {
        reason: '',
      },
    }
  }

  buildOps () {
    return this._opBuilder()
  }

  /** @param {VerificationCDDRecord} source */
  populate (source) {
    const {
      geoRisk,
      productRisk,
      commonRisk,
      documents,
      pep,
      checkList,
      riskAssmnt,
      imagesData,
    } = source

    this.attrs.imagesData = {
      documentImages: imagesData.documentImages,
      selfie: imagesData.selfie,
      documentDetails: imagesData.documentDetails,
    }

    this.attrs.documents = {
      name: documents.name,
      placeOfBirth: documents.placeOfBirth,
      birthDate: documents.birthDate,
      nationalities: documents.nationalities,
      address: documents.address,
      email: documents.email,
    }
    this.attrs.commonRisk = {
      qus01: commonRisk.qus01,
      qus02: commonRisk.qus02,
      qus03: commonRisk.qus03,
    }
    this.attrs.productRisk = {
      qus01: productRisk.qus01,
      qus02: productRisk.qus02,
      qus03: productRisk.qus03,
      qus04: productRisk.qus04,
      qus05: productRisk.qus05,
      qus06: productRisk.qus06,
    }
    this.attrs.geoRisk = {
      qus01: geoRisk.qus01,
    }
    this.attrs.pep = {
      qus01: pep.qus01,
      qus02: pep.qus02,
      pepInfo: pep.pepInfo,
      countryRisk: pep.countryRisk,
    }
    this.attrs.checkList = {
      qus03: checkList.qus03,
      qus04: checkList.qus04,
      description: checkList.description,
      riskFactors: checkList.riskFactors,
    }
    this.attrs.riskAssmnt = {
      comments: riskAssmnt.comments,
      subscriber: riskAssmnt.subscriber,
      signatureDate: riskAssmnt.signatureDate,
      lowRisk: riskAssmnt.lowRisk,
      generalRisk: riskAssmnt.generalRisk,
      highRisk: riskAssmnt.highRisk,
    }
    this.attrs.rejectReason = {
      reason: '',
    }
  }

  _buildOpCreate () {
    return {
      documents: this._documentsDetails(),
      imagesData: this._imagesDataDetails(),
      commonRisk: this._commonRiskDetails(),
      geoRisk: this._geoRiskDetails(),
      productRisk: this._productRiskDetails(),
      pep: this._pepDetails(),
      checkList: this._checkListDetails(),
      riskAssmnt: this._riskAssmntDetails(),
      rejectReason: this.attrs.rejectReason,
    }
  }

  _commonRiskDetails () {
    const { commonRisk } = this.attrs

    return {
      qus01: str(commonRisk.qus01),
      qus02: str(commonRisk.qus02),
      qus03: str(commonRisk.qus03),
    }
  }

  _geoRiskDetails () {
    const { geoRisk } = this.attrs

    return {
      qus01: str(geoRisk.qus01),
    }
  }

  _productRiskDetails () {
    const { productRisk } = this.attrs

    return {
      qus01: str(productRisk.qus01),
      qus02: str(productRisk.qus02),
      qus03: str(productRisk.qus03),
      qus04: str(productRisk.qus04),
      qus05: str(productRisk.qus05),
      qus06: str(productRisk.qus06),
    }
  }

  _documentsDetails () {
    const { documents } = this.attrs

    return {
      name: documents.name,
      placeOfBirth: documents.placeOfBirth,
      birthDate: documents.birthDate,
      nationalities: documents.nationalities,
      address: documents.address,
      email: documents.email,
    }
  }

  _imagesDataDetails () {
    const { imagesData } = this.attrs

    return {
      documentImages: imagesData.documentImages,
      selfie: imagesData.selfie,
      documentDetails: imagesData.documentDetails,
    }
  }

  _pepDetails () {
    const { pep } = this.attrs

    return {
      qus01: str(pep.qus01),
      qus02: str(pep.qus02),
      pepInfo: str(pep.pepInfo),
      countryRisk: str(pep.countryRisk),
    }
  }

  _checkListDetails () {
    const { checkList } = this.attrs

    return {
      qus03: str(checkList.qus03),
      qus04: str(checkList.qus04),
      description: str(checkList.description),
      riskFactors: str(checkList.riskFactors),
    }
  }

  _riskAssmntDetails () {
    const { riskAssmnt } = this.attrs

    return {
      comments: str(riskAssmnt.comments),
      subscriber: str(riskAssmnt.subscriber),
      signatureDate: str(riskAssmnt.signatureDate),
      lowRisk: riskAssmnt.lowRisk,
      generalRisk: riskAssmnt.generalRisk,
      highRisk: riskAssmnt.highRisk,
    }
  }

  _isDocumentDetailsEmpty () {
    const { documentDetails } = this.attrs.imagesData
    const result = (documentDetails && documentDetails.length === 0) ||
      (documentDetails && documentDetails.__ob__ && documentDetails.__ob__.vmCount === 0)

    return result
  }

  _isDocumentImagesEmpty () {
    const { documentImages } = this.attrs.imagesData
    const result = (documentImages && documentImages.length === 0) ||
      (documentImages && documentImages.__ob__ && documentImages.__ob__.vmCount === 0)

    return result
  }
}
